import React, {useContext} from 'react'

import PropTypes from 'prop-types'

import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'

const MONTHS_IN_YEAR = 12

const PanelAdDetailsVehicleData = ({ad = {}, isSubscription = false, pageName, vehicleData = {}}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {environmentalLabels, ENVIRONMENTAL_LABEL_ICONS} = config.get('search')
  const urlStatics = config.get('urlStatics')

  const {transmissionTypes = [], fuelTypes = [], specificFueltypes = []} = config.get('search')

  const {
    availableColors = [],
    bodyType,
    color,
    cubicCapacity,
    doors,
    emissions,
    environmentalLabel: environmentalLabelId,
    fuelTypeId,
    hasOfficialWarranty,
    hasWarranty,
    hp,
    km,
    powerElectric,
    province,
    seats,
    transmissionTypeId,
    warrantyMonths,
    warrantyText,
    year
  } = vehicleData

  const showAvailableColors = availableColors.length > 0 && isSubscription
  const showColor = color && !isSubscription
  const showKm = km && !isSubscription
  const showProvince = province && !isSubscription
  const showWarranty = hasWarranty && !isSubscription

  const environmentalLabel = environmentalLabels.find(label => label.id === environmentalLabelId)

  const hasEnvironmentalLabel = Boolean(environmentalLabel)

  const environmentalLabelText = environmentalLabel && i18n.t(environmentalLabel.label)

  const environmentalLabelIcon =
    environmentalLabel && `${urlStatics}${ENVIRONMENTAL_LABEL_ICONS[environmentalLabel.id]}`

  const _getAvailableColorsElement = availableColors => {
    return (
      <>
        {availableColors.map((availableColor, index, array) => {
          return (
            <>
              <strong>{i18n.t(availableColor)}</strong>
              <>{index < array.length - 1 ? ', ' : null}</>
            </>
          )
        })}
      </>
    )
  }

  const _getTransmissionTypeName = () => {
    const transmissionType = transmissionTypes.find(({id}) => Number(id) === Number(transmissionTypeId))
    return transmissionType ? transmissionType.label : null
  }
  const transmissionType = _getTransmissionTypeName()

  const _getFuelType = () => {
    const hasSpecificFuelTypes = Boolean(specificFueltypes.length)
    const availableFuelTypes = hasSpecificFuelTypes ? specificFueltypes : fuelTypes
    const fuelType = availableFuelTypes.find(fuelType => fuelType.id === fuelTypeId)
    return fuelType ? fuelType.label : null
  }
  const fuelType = _getFuelType()

  const _getWarrantyPeriod = ({months, forceMonths = false}) => {
    if (forceMonths) return i18n.t('COMMON_N_MONTHS', months)
    const showYear = months % MONTHS_IN_YEAR === 0
    return showYear ? i18n.t('COMMON_N_YEARS', months / MONTHS_IN_YEAR) : i18n.t('COMMON_N_MONTHS', months)
  }

  const _getWarrantyLiteral = () => {
    const warrantyLiteralMonths = _getWarrantyPeriod({
      months: warrantyMonths,
      forceMonths: true
    })
    const warrantyLiteralYears = _getWarrantyPeriod({
      months: warrantyMonths
    })
    const warrantyPeriod = <strong>{`${warrantyLiteralMonths} (${warrantyLiteralYears})`}</strong>

    if (hasOfficialWarranty)
      return (
        <>
          {i18n.t('COMMON.OFFICIAL_WARRANTY')}&nbsp;{warrantyPeriod}
        </>
      )

    if (warrantyMonths)
      return (
        <>
          {i18n.t('COMMON.WARRANTY')}&nbsp;{warrantyPeriod}
        </>
      )
    if (warrantyText)
      return (
        <>
          <strong>
            {i18n.t('COMMON.WARRANTY')}&nbsp;{warrantyText}
          </strong>
        </>
      )
  }

  const vehicleDataItems = []
  // fuelType has to go first when on a Subscription Ad
  fuelType && isSubscription && vehicleDataItems.push(<strong>{i18n.t(fuelType)}</strong>)
  year && vehicleDataItems.push(<strong>{year}</strong>)
  showKm &&
    vehicleDataItems.push(
      <>
        <strong>{i18n.n(km)}</strong>
        &nbsp;{i18n.t('COMMON.UNIT_KMS')}
      </>
    )
  showProvince &&
    vehicleDataItems.push(
      <>
        <strong>{province}</strong>
      </>
    )
  // transmissionType has to go here when on a Subscription Ad
  transmissionType &&
    isSubscription &&
    vehicleDataItems.push(
      <>
        {i18n.t('COMMON.TRANSMISSION')}&nbsp;
        <strong>{i18n.t(transmissionType)}</strong>
      </>
    )
  bodyType && vehicleDataItems.push(<strong>{i18n.t(bodyType)}</strong>)
  // transmissionType has to go here when on a non Subscription Ad
  transmissionType &&
    !isSubscription &&
    vehicleDataItems.push(
      <>
        {i18n.t('COMMON.TRANSMISSION')}&nbsp;
        <strong>{i18n.t(transmissionType)}</strong>
      </>
    )
  doors &&
    vehicleDataItems.push(
      <>
        <strong>{doors}</strong>
        &nbsp;{i18n.t('COMMON.DOORS')}
      </>
    )
  seats &&
    vehicleDataItems.push(
      <>
        <strong>{seats}</strong>
        &nbsp;{i18n.t('COMMON.SEATS')}
      </>
    )
  cubicCapacity &&
    vehicleDataItems.push(
      <>
        <strong>{cubicCapacity}</strong>
        &nbsp;{i18n.t('COMMON.UNIT_CC')}
      </>
    )
  powerElectric &&
    vehicleDataItems.push(
      <>
        <strong>{powerElectric}</strong>
        &nbsp;{i18n.t('COMMON.UNIT_KW')}
      </>
    )
  hp &&
    vehicleDataItems.push(
      <>
        <strong>{hp}</strong>
        &nbsp;{i18n.t('COMMON.UNIT_HP')}
      </>
    )
  showColor && vehicleDataItems.push(<strong>{i18n.t(color)}</strong>)
  showAvailableColors && vehicleDataItems.push(_getAvailableColorsElement(availableColors))
  emissions &&
    vehicleDataItems.push(
      <>
        <strong>{emissions}</strong>
        &nbsp;{i18n.t('COMMON.UNIT_EMISSIONS')}
      </>
    )
  // fuelType goes here on a non Subscription Ad
  fuelType && !isSubscription && vehicleDataItems.push(<strong>{i18n.t(fuelType)}</strong>)
  showWarranty && vehicleDataItems.push(_getWarrantyLiteral())
  hasEnvironmentalLabel &&
    vehicleDataItems.push(
      <div className="mt-PanelAdDetails-dataItemEcoLabel">
        <strong>{environmentalLabelText}</strong>
        <div className="mt-PanelAdDetails-dataItemEcoLabelImageWrapper">
          <AtomImage
            className="mt-PanelAdDetails-dataItemEcoLabelImage"
            src={environmentalLabelIcon}
            alt={environmentalLabelText}
          />
        </div>
      </div>
    )

  return (
    <ul className="mt-PanelAdDetails-data">
      {vehicleDataItems.map((item, index) => {
        return (
          <li className="mt-PanelAdDetails-dataItem" key={index}>
            {item}
          </li>
        )
      })}
    </ul>
  )
}

PanelAdDetailsVehicleData.propTypes = {
  ad: PropTypes.object,
  isSubscription: PropTypes.bool,
  pageName: PropTypes.string,
  vehicleData: PropTypes.object
}

export default PanelAdDetailsVehicleData
